'use client';

import React from 'react';
import { Box, Typography, Button, Container, Stack } from '@mui/material';
import { ErrorOutline, Home, Refresh } from '@mui/icons-material';
import { useTranslation } from '@/kit/useTranslation';

// Class component to catch errors
class GenericErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[GenericErrorBoundary] Caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || null;
    }
    return this.props.children;
  }
}

// Wrapper functional component
function GenericErrorBoundary({ children, fallback }) {
  const { t } = useTranslation();

  const translatedFallback = fallback || (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f9fafb',
      }}
    >
      <Container maxWidth="sm">
        <Stack spacing={3} alignItems="center" textAlign="center">
          <ErrorOutline sx={{ fontSize: 80, color: '#ef4444' }} />
          <Typography variant="h4" fontWeight={700} color="#111827">
            {t('errorBoundary.oopsSomethingWentWrong')}
          </Typography>
          <Typography variant="body1" color="#6b7280">
            {t('errorBoundary.unexpectedError')}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => window.location.reload()}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                px: 3,
              }}
            >
              {t('errorBoundary.refreshPage')}
            </Button>
            <Button
              variant="contained"
              startIcon={<Home />}
              onClick={() => (window.location.href = '/')}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                px: 3,
                bgcolor: '#000',
                '&:hover': { bgcolor: '#1a1a1a' },
              }}
            >
              {t('errorBoundary.goHome')}
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );

  return (
    <GenericErrorBoundaryClass fallback={translatedFallback}>{children}</GenericErrorBoundaryClass>
  );
}

export default GenericErrorBoundary;
